import { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { ExtensionAuthErrors } from '@/lib/errors';

// Debug flag - set to true to see detailed logs
const DEBUG = true;

/**
 * Debug logger that only logs when DEBUG is true
 */
const debugLog = {
  info: (...args: any[]) => {
    if (DEBUG) {
      console.log('%c[Extension Sync]', 'color: #0ea5e9; font-weight: bold;', ...args);
    }
  },
  warn: (...args: any[]) => {
    if (DEBUG) {
      console.warn('%c[Extension Sync]', 'color: #eab308; font-weight: bold;', ...args);
    }
  },
  error: (...args: any[]) => {
    if (DEBUG) {
      console.error('%c[Extension Sync]', 'color: #dc2626; font-weight: bold;', ...args);
    }
  }
};

declare global {
  interface Window {
    chrome?: {
      runtime?: {
        sendMessage: (
          extensionId: string,
          message: any,
          callback?: (response: any) => void
        ) => void;
        lastError?: {
          message?: string;
        };
      };
    };
  }
}

// Validate Chrome extension ID format (32 characters, lowercase letters and numbers)
export const isValidExtensionId = (id: string): boolean => {
  const trimmed = id.trim();
  return /^[a-z0-9]{32}$/.test(trimmed);
};

// Get allowed extension IDs from environment
const getExtensionIds = (): string[] => {
  // Try the new comma-separated format first
  const idsString = import.meta.env.VITE_CHROME_EXTENSION_IDS;
  
  // If that's not available, try the individual variables
  const legacyIds = [
    import.meta.env.VITE_CHROME_EXTENSION_ID_PRODUCTION,
    import.meta.env.VITE_CHROME_EXTENSION_ID_DEV14,
    import.meta.env.VITE_CHROME_EXTENSION_ID_DEV16,
  ];

  debugLog.info('Loading extension IDs:', {
    newFormat: idsString,
    legacyFormat: legacyIds,
    env: import.meta.env
  });
  
  // If we have the new format, use it
  if (typeof idsString === 'string' && idsString.length > 0) {
    const ids = idsString
      .split(',')
      .map((id: string) => id.trim())
      .filter((id: string) => id.length > 0 && isValidExtensionId(id));
    
    debugLog.info('Using new format IDs:', ids);
    return ids;
  }
  
  // Otherwise, fall back to legacy format
  const ids = legacyIds
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .map((id: string) => id.trim())
    .filter((id: string) => isValidExtensionId(id));
  
  debugLog.info('Using legacy format IDs:', ids);
  return ids;
};

// Chrome extension IDs that are allowed to receive auth state messages
const ALLOWED_EXTENSION_IDS = getExtensionIds();

// Log configured IDs
debugLog.info('Configured extension IDs:', {
  allowed: ALLOWED_EXTENSION_IDS,
  count: ALLOWED_EXTENSION_IDS.length
});

interface AuthStateMessage {
  type: 'AUTH_STATE_CHANGE';
  payload: {
    event: AuthChangeEvent;
    sessionInfo: {
      user_id: string;
      avatar_url: string;
      email: string;
    } | null;
    session: {
      access_token: string;
      refresh_token: string;
      expires_at: number;
    } | null;
  };
}

// Events that should trigger a broadcast to extensions
const BROADCAST_EVENTS: AuthChangeEvent[] = ['SIGNED_IN', 'SIGNED_OUT', 'TOKEN_REFRESHED'];

/**
 * Creates an auth state message from a session
 */
function createAuthStateMessage(event: AuthChangeEvent, session: Session | null): AuthStateMessage {
  if (!session) {
    return {
      type: 'AUTH_STATE_CHANGE',
      payload: {
        event,
        sessionInfo: null,
        session: null
      }
    };
  }

  // Ensure required fields exist
  if (!session.user.email) {
    throw new Error('User email is required but missing from session');
  }

  if (!session.expires_at) {
    throw new Error('Session expiration is required but missing');
  }

  return {
    type: 'AUTH_STATE_CHANGE',
    payload: {
      event,
      sessionInfo: {
        user_id: session.user.id,
        avatar_url: session.user.user_metadata.avatar_url || session.user.user_metadata.picture || '',
        email: session.user.email
      },
      session: {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: session.expires_at
      }
    }
  };
}

/**
 * Sends an auth state message to a specific extension
 */
async function sendMessageToExtension(extensionId: string, event: AuthChangeEvent, session: Session | null): Promise<void> {
  const TIMEOUT_MS = 5000;

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      debugLog.warn(`Message to extension ${extensionId} timed out after ${TIMEOUT_MS}ms`);
      reject(new Error(ExtensionAuthErrors.TIMEOUT));
    }, TIMEOUT_MS);

    try {
      const message = createAuthStateMessage(event, session);

      // Log the message we're about to send (redact sensitive data in production)
      debugLog.info('Sending message:', {
        extensionId,
        message: DEBUG ? message : {
          ...message,
          payload: {
            ...message.payload,
            session: message.payload.session ? {
              ...message.payload.session,
              access_token: '[REDACTED]',
              refresh_token: '[REDACTED]'
            } : null
          }
        }
      });

      if (!window.chrome?.runtime?.sendMessage) {
        clearTimeout(timeoutId);
        reject(new Error(ExtensionAuthErrors.NO_EXTENSION));
        return;
      }

      window.chrome.runtime.sendMessage(
        extensionId,
        message,
        (response) => {
          clearTimeout(timeoutId);
          
          if (window.chrome?.runtime?.lastError) {
            const error = window.chrome.runtime.lastError;
            
            // If the error is just that the receiving end doesn't exist for response
            // but we know the message was sent (based on extension logs),
            // we can consider this a success
            if (error.message?.includes('Receiving end does not exist')) {
              debugLog.info('Extension received message but could not respond - considering successful');
              resolve();
              return;
            }
            
            debugLog.error('Chrome runtime error:', error);
            reject(new Error(error.message || ExtensionAuthErrors.UNKNOWN));
            return;
          }
          
          debugLog.info('Message sent successfully:', response);
          resolve();
        }
      );
    } catch (error) {
      clearTimeout(timeoutId);
      reject(error);
    }
  });
}

/**
 * Manually syncs auth state with a specific extension with retries
 */
export async function syncExtension(extensionId: string, session: Session | null): Promise<void> {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000; // 1 second

  if (!isValidExtensionId(extensionId.trim())) {
    throw new Error(ExtensionAuthErrors.INVALID_EXTENSION_ID);
  }

  if (!session) {
    throw new Error(ExtensionAuthErrors.NO_SESSION);
  }

  const attemptSync = async (attempt: number = 1): Promise<void> => {
    try {
      debugLog.info(`Attempting to sync extension (attempt ${attempt}/${MAX_RETRIES})...`);
      await sendMessageToExtension(extensionId.trim(), 'SIGNED_IN', session);
      debugLog.info('Sync successful');
    } catch (error) {
      debugLog.warn(`Sync attempt ${attempt} failed:`, error);

      // If we have retries left and it's a retryable error, try again
      if (attempt < MAX_RETRIES && error instanceof Error && (
        error.message?.includes('message port closed') ||
        error.message?.includes('Receiving end does not exist') ||
        error.message === ExtensionAuthErrors.TIMEOUT
      )) {
        debugLog.info(`Retrying in ${RETRY_DELAY}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return attemptSync(attempt + 1);
      }
      
      throw error;
    }
  };

  return attemptSync();
}

/**
 * Broadcasts auth state changes to allowed Chrome extensions
 */
function broadcastToExtensions(event: AuthChangeEvent, session: Session | null) {
  // Skip if the event is not one we want to broadcast
  if (!BROADCAST_EVENTS.includes(event)) {
    debugLog.info(`Skipping broadcast for event: ${event}`);
    return;
  }

  // Skip if no valid extension IDs are configured
  if (ALLOWED_EXTENSION_IDS.length === 0) {
    debugLog.warn('No valid extension IDs configured - skipping auth broadcast');
    return;
  }

  // Log available extension IDs
  debugLog.info('Broadcasting to extensions:', ALLOWED_EXTENSION_IDS);

  // Send message to each allowed extension
  ALLOWED_EXTENSION_IDS.forEach(extensionId => {
    sendMessageToExtension(extensionId, event, session)
      .catch(error => {
        debugLog.error(`Failed to send message to extension ${extensionId}:`, error);
      });
  });
}

/**
 * Initializes the extension sync module
 * This should be called once when the app starts
 */
export function initializeExtensionSync() {
  debugLog.info('Initializing extension sync module...');
  
  // Subscribe to auth state changes
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    debugLog.info('Auth state changed:', event);
    broadcastToExtensions(event, session);
  });

  debugLog.info('Extension sync initialized successfully');

  return () => {
    debugLog.info('Cleaning up extension sync...');
    subscription.unsubscribe();
  };
} 